/**
 * LivingSigil — animated astrology sigil brand component.
 *
 * Renders an SVG with:
 *   • 3 concentric rings (outer gradient, mid, inner dashed)
 *   • 60 tick marks (every 5th larger) on the mid ring
 *   • 12 zodiac glyphs between outer and mid rings
 *   • Animated radial-gradient core that "breathes"
 *
 * States: 'breathing' (default slow pulse), 'pulse' (fast), 'reveal' (entry animation).
 * Archetype prop maps to a palette driving the drop-shadow filter.
 *
 * All colors via CSS custom properties — no hardcoded hex values.
 * No design/ imports. No logo-transparent.png dependency.
 * prefers-reduced-motion block in LivingSigil.module.css.
 */

import { useMemo } from 'react';
import styles from './LivingSigil.module.css';

// --------------------------------------------------------------------------
// Types
// --------------------------------------------------------------------------

export type SigilState = 'breathing' | 'pulse' | 'reveal';
export type SigilPalette = 'gold' | 'silver' | 'rose' | 'violet';

export interface LivingSigilProps {
  /** SVG/container size in px. Clamped to [160, 320]. Default 220. */
  size?: number;
  /** Animation state. Default 'breathing'. */
  state?: SigilState;
  /** Optional archetype string — maps to palette + spotlight glyph accent. */
  archetype?: string;
  className?: string;
}

// --------------------------------------------------------------------------
// Archetype → palette map (P1 subset — extend in later milestones)
// --------------------------------------------------------------------------

const ARCHETYPE_TO_PALETTE: Record<string, SigilPalette> = {
  'whimsigoth-witch': 'silver',
  'cottage-mystic': 'gold',
  'cyber-y2k': 'violet',
  'celestial-romantic': 'rose',
};

// --------------------------------------------------------------------------
// Component
// --------------------------------------------------------------------------

export function LivingSigil({
  size = 220,
  state = 'breathing',
  archetype,
  className,
}: LivingSigilProps): JSX.Element {
  // Clamp size to design rules: [160, 320]
  const clamped = Math.max(160, Math.min(320, size));
  const palette: SigilPalette =
    archetype ? (ARCHETYPE_TO_PALETTE[archetype] ?? 'gold') : 'gold';

  // Ring radii derived from clamped size
  const r1 = clamped / 2 - 4;   // outer ring
  const r2 = r1 - 16;            // mid ring
  const r3 = r1 - 36;            // inner dashed ring

  // Zodiac glyphs — stable reference
  const glyphs = useMemo(
    () => ['♈', '♉', '♊', '♋', '♌', '♍', '♎', '♏', '♐', '♑', '♒', '♓'],
    [],
  );

  // Unique gradient id prefix to avoid collisions when multiple instances render
  const uid = `sigil-${clamped}-${palette}`;

  // CSS module state/palette classes (undefined-safe — module may not define all)
  const stateClass = (styles as Record<string, string>)[`state-${state}`] ?? '';
  const paletteClass = (styles as Record<string, string>)[`palette-${palette}`] ?? '';

  return (
    <div
      role="img"
      aria-label={archetype ? `Living Sigil for ${archetype}` : 'Living Sigil'}
      data-sigil-state={state}
      data-sigil-palette={palette}
      className={[styles.root, stateClass, paletteClass, className ?? '']
        .filter(Boolean)
        .join(' ')}
      style={{ width: clamped, height: clamped }}
    >
      <svg
        width={clamped}
        height={clamped}
        viewBox={`0 0 ${clamped} ${clamped}`}
        className={styles.svg}
        aria-hidden="true"
      >
        <defs>
          {/* Radial gradient for the breathing core */}
          <radialGradient id={`${uid}-core`} cx="0.5" cy="0.4" r="0.6">
            <stop offset="0%"   stopColor="var(--cream)"      stopOpacity="0.95" />
            <stop offset="30%"  stopColor="var(--gold-bright)" stopOpacity="0.85" />
            <stop offset="75%"  stopColor="var(--neb-purple)"  stopOpacity="0.65" />
            <stop offset="100%" stopColor="var(--indigo)"      stopOpacity="1" />
          </radialGradient>

          {/* Linear gradient for the outer ring stroke */}
          <linearGradient id={`${uid}-ring`} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%"   stopColor="var(--gold-bright)" />
            <stop offset="100%" stopColor="var(--amber)" />
          </linearGradient>
        </defs>

        {/* Outer ring */}
        <circle
          cx={clamped / 2}
          cy={clamped / 2}
          r={r1}
          fill="none"
          stroke={`url(#${uid}-ring)`}
          strokeWidth="1"
        />

        {/* Mid ring */}
        <circle
          cx={clamped / 2}
          cy={clamped / 2}
          r={r2}
          fill="none"
          stroke="var(--gold)"
          strokeOpacity="0.5"
          strokeWidth="0.5"
        />

        {/* Inner dashed ring */}
        <circle
          cx={clamped / 2}
          cy={clamped / 2}
          r={r3}
          fill="none"
          stroke="var(--gold)"
          strokeOpacity="0.3"
          strokeWidth="0.4"
          strokeDasharray="2 4"
        />

        {/* 60 tick marks, every 6°; every 5th is taller */}
        {Array.from({ length: 60 }).map((_, i) => {
          const angle = (i * 6 - 90) * (Math.PI / 180);
          const big = i % 5 === 0;
          const outerR = r2;
          const innerR = r2 - (big ? 7 : 3);
          return (
            <line
              key={i}
              x1={clamped / 2 + Math.cos(angle) * outerR}
              y1={clamped / 2 + Math.sin(angle) * outerR}
              x2={clamped / 2 + Math.cos(angle) * innerR}
              y2={clamped / 2 + Math.sin(angle) * innerR}
              stroke="var(--gold)"
              strokeOpacity={big ? 0.85 : 0.4}
              strokeWidth="0.6"
            />
          );
        })}

        {/* 12 zodiac glyphs between outer and mid rings */}
        {glyphs.map((glyph, i) => {
          const angle = (i * 30 - 75) * (Math.PI / 180);
          const glyphR = (r1 + r2) / 2;
          return (
            <text
              key={i}
              x={clamped / 2 + Math.cos(angle) * glyphR}
              y={clamped / 2 + Math.sin(angle) * glyphR + 4}
              textAnchor="middle"
              fontSize={Math.round(clamped / 22)}
              fontFamily="Spectral, serif"
              fill="var(--gold-bright)"
              opacity="0.7"
            >
              {glyph}
            </text>
          );
        })}

        {/* Animated core — logo image; class drives breathing / pulse / reveal via CSS */}
        {(() => {
          const logoR = r3 - 8;
          return (
            <image
              href="/logo.webp"
              x={clamped / 2 - logoR}
              y={clamped / 2 - logoR}
              width={logoR * 2}
              height={logoR * 2}
              preserveAspectRatio="xMidYMid meet"
              className={styles.core}
            />
          );
        })()}
      </svg>
    </div>
  );
}
